// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CompanyService } from '@lfx/core/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { userTechnicalRow } from '@lfx/core/gql';
import { Company } from '@lfx/core/models';

@Component({
  selector: 'lfx-profile-snapshot-employee-technical',
  templateUrl: './profile-snapshot-employee-technical.component.html',
  styleUrls: ['./profile-snapshot-employee-technical.component.scss'],
  standalone: false,
})
export class ProfileSnapshotEmployeeTechnicalComponent
  implements OnInit, OnDestroy
{
  @Input() timeRange$: Observable<string>;
  @Input() userId: string;

  private stateSubject = new BehaviorSubject<
    'LOADING' | 'ERROR' | 'SUCCESS' | 'EMPTY'
  >('LOADING');

  state$ = this.stateSubject.asObservable();
  company$: Observable<Company>;
  noDataMessage = '';
  metrics = [];
  useQuarter = false;

  technicalRowMap = (row: userTechnicalRow) => ({
    ['PR Opened']: row.propened,
    ['PR Merged']: row.prmerged,
    ['Issues Opened']: row.issuesopen,
    ['Issues Closed']: row.issuesclosed,
    ['Commit Activity']: row.commits,
  });

  userTechnicalRow: userTechnicalRow[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private spinner: NgxSpinnerService,
    private companyService: CompanyService
  ) {
    this.company$ = this.companyService.getMyCompany();
  }

  ngOnInit(): void {
    this.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state === 'LOADING') {
        this.spinner.show('technicalActivitiesSpinner');
      } else {
        this.spinner.hide('technicalActivitiesSpinner');
      }

      if (state === 'ERROR') {
        this.noDataMessage = 'Error fetching technical activities';
      }

      if (state === 'EMPTY') {
        this.noDataMessage = 'No technical activities during this period';
      }
    });

    combineLatest([this.company$, this.timeRange$])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([company, timeRange]) => {
          this.stateSubject.next('LOADING');
          this.useQuarter = timeRange === 'alltime';

          return this.companyService.getEmployeeTechnicalActivity(
            company.id,
            this.userId,
            timeRange,
            this.companyService.isAllOrgsRollup()
          );
        })
      )
      .subscribe({
        next: data => {
          if (!data || !data.chart || !data.chart.length) {
            this.stateSubject.next('EMPTY');
            this.userTechnicalRow = [];
            this.metrics = [];

            return;
          }

          this.userTechnicalRow = data.chart;

          this.metrics = [
            {
              value: data.stats?.prOpenedCount || 0,
              description: 'PR Opened',
            },
            {
              value: data.stats?.prMergedCount || 0,
              description: 'PR Merged',
            },
            {
              value: data.stats?.issueOpenedCount || 0,
              description: 'Issues Opened',
            },
            {
              value: data.stats?.issueClosedCount || 0,
              description: 'Issues Closed',
            },
            {
              value: data.stats?.commitCount || 0,
              description: 'Commit Activity',
            },
          ];

          this.stateSubject.next('SUCCESS');
        },
        error: () => {
          this.stateSubject.next('ERROR');
          this.userTechnicalRow = [];
          this.metrics = [];
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stateSubject.complete();
  }
}
